/*
 * TextAreaField.tsx
 * Un composant de champ de texte avec label, gestion des erreurs et styles personnalisés.
 */

// Import React
import React from 'react';
import { Skeleton } from '@radix-ui/themes';
import '../../styles/global.css';

// Définition des props
interface TextAreaFieldProps {
	label: string;
	name: string;
	value: string | undefined;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	error?: string | null;
	placeholder?: string;
	required?: boolean;
	visibility?: boolean;
	readOnly?: boolean;
	loading?: boolean;
}

// TextAreaField component
const TextAreaField: React.FC<TextAreaFieldProps> = ({
	label,
	name,
	value,
	onChange,
	error,
	placeholder,
	required,
	visibility,
	readOnly,
	loading,
}) => {
	// Génération d'un identifiant unique pour le textarea
	const textAreaId = `textarea-${name}`; // unique pour chaque champ
	// Gestion de la visibilité à true si non définie
	const visibilityTextArea = visibility ?? true;
	// Gestion de la propriété readOnly
	const readOnlyTextArea = readOnly ?? false;

	// Rendu du composant
	return (
		<div style={{ display: visibilityTextArea ? 'block' : 'none' }}>
			<label
				htmlFor={textAreaId}
				className="block text-sm font-medium text-gray-700 mb-1"
			>
				{label}
			</label>
			{loading ? (
				<Skeleton
					className="w-full h-16 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
					style={{
						backgroundSize: '200% 100%',
						animation: 'shimmer 1.5s infinite linear',
					}}
				/>
			) : (
				<textarea
					id={textAreaId}
					name={name}
					value={value || ''}
					onChange={onChange}
					placeholder={placeholder}
					className={`
						w-full h-50 border p-2 rounded bg-gray-100
						focus:outline-none focus:ring-2 focus:ring-blue-500
						placeholder:italic
						${readOnlyTextArea ? 'text-gray-400 cursor-not-allowed bg-gray-200' : ''}`}
					readOnly={readOnlyTextArea}
					required={required}
				/>
			)}
			{error && <p className="text-red-600 text-sm mt-1">{error}</p>}
		</div>
	);
};

// Export the component
export default TextAreaField;
